import { Form, Radio } from 'antd';
import { useWatch } from 'antd/es/form/Form';
import { isDefined } from 'class-validator';
import fileDownload from 'js-file-download';
import { useEffect, useState } from 'react';
import FormComponent from 'src/app/components/FormComponent/FormComponent';
import SearchableSelect from 'src/app/components/SearchableSelect/SearchableSelect';
import {
  convertDateToReadableText,
  exportFileSendMail,
} from 'src/app/forms/ExportInterestForm/export-interest-form.config';
import styles from 'src/app/forms/ExportInterestForm/exportInterest.module.scss';
import { IFormProps } from 'src/app/forms/form.config';
import { getEnumKeyByValue } from 'src/app/helpers/enum.helper';
import { pdfExport } from 'src/app/helpers/pdf.helper';
import { getInterestNameFromLookup } from 'src/app/helpers/service.helper';
import { convertInteractionsToTableData } from 'src/app/pages/InterestsPage/RelationPage/relation-interest-config';
import {
  useExportEnterpriseIdentifiedExcellMutation,
  useLazyGetEnterprisesAllQuery,
} from 'src/app/redux/company/companySlice';
import { useAppSelector } from 'src/app/redux/hook';
import { useLazyGetInteractionsQuery } from 'src/app/redux/interaction/interactionSlice';
import {
  useExportPersonIdentifiedExcellMutation,
  useLazyGetPersonsIdentifiedAllQuery,
} from 'src/app/redux/interests/persons/personIdentifiedSlice';
import {
  useExportVehicleIdentifiedExcellMutation,
  useLazyGetVehiclesIdentifiedAllQuery,
} from 'src/app/redux/interests/vehicles/vehicleIdentifiedSlice';
import {
  useExportLocationIdentifiedExcellMutation,
  useLazyGetLocationsIdentifiedAllQuery,
} from 'src/app/redux/locations/locationsIdentifiedSlice';
import { NSearchLoggerType } from 'src/app/redux/nSearch/nSearch-slice-type';
import { useExportNSearchExcellMutation } from 'src/app/redux/nSearch/nSearchSlice';
import { useExportRelationExcellMutation } from 'src/app/redux/relation/relationSlice';
import {
  useExportSensorIdentifiedExcellMutation,
  useLazyGetSensorsIdentifiedQuery,
} from 'src/app/redux/sensors/sensorIdentifiedSlice';
import { useExportStudentExcellMutation, useLazyGetStudentsQuery } from 'src/app/redux/students/studentsSlice';
import {
  useExportTopicIdentifiedExcellMutation,
  useLazyGetTopicsIdentifiedQuery,
} from 'src/app/redux/topics/topicIdentifiedSlice';
import { TransactionIdentifiedDataType } from 'src/app/redux/transactionOfInterests/transactions-slice.type';
import {
  useExportTransactionIdentifiedExcellMutation,
  useLazyGetTransactionsIdentifiedQuery,
} from 'src/app/redux/transactionOfInterests/transactionsSlice';
import * as userReduxType from 'src/app/redux/user/user.redux.type';
import { useGetUsersQuery } from 'src/app/redux/user/userSlice';
import { EnterpriseIdentifiedDataType } from 'src/app/services/interest/Enterprise/enterprise-service.type';
import { LocationIdentifiedDataType } from 'src/app/services/interest/Location/location-service.type';
import { TopicIdentifiedDataType } from 'src/app/services/interest/Topic/topic-service.type';
import { getNSearchLogs } from 'src/app/services/nSearchBackend/nSearchLogger-service';
import { StudentDataType } from 'src/app/services/student/student.service.type';
import { PersonStatus, SensorCategoryType, SensorType } from 'src/enums/nginr-type.enum';
import { PersonIdentified } from 'src/types/person.type';
import { SensorIdentified } from 'src/types/sensor.type';
import { VehicleIdentified } from 'src/types/vehicle.type';
import tinycolor from 'tinycolor2';

export type InterestExportFormType = {
  filteredInterestIds: string[];
  interestName:
    | 'person'
    | 'vehicle'
    | 'location'
    | 'sensor'
    | 'enterprise'
    | 'topic'
    | 'nSearch'
    | 'relation'
    | 'student'
    | 'transaction';
};

const allInterestTableValues = (dataValues: any, interestName: string) => {
  if (interestName === 'person') {
    const interest: PersonIdentified[] = dataValues;
    const values = interest.map((person) => [
      { field: person.name, cellWidth: 35 },
      { field: person.surname, cellWidth: 35 },
      { field: person.email, cellWidth: 50 },
      // { field: person.description, cellWidth: 25 },
      { field: person.organization, cellWidth: 25 },
      { field: getEnumKeyByValue(PersonStatus, person.status), cellWidth: 15 },
      { field: person.height, cellWidth: 15 },
      { field: person.weight, cellWidth: 15 },
      { field: person.nationality?.name, cellWidth: 25 },
      { field: person.collar?.name, cellWidth: 20 },
      { field: person.age?.name, cellWidth: 20 },
      { field: person.gender?.name, cellWidth: 15 },
    ]);
    const headers = [
      'Name',
      'Surname',
      'Email',
      // 'Description',
      'Organization',
      'Status',
      'Height',
      'Weight',
      'Nationality',
      'Collar',
      'Age',
      'Gender',
    ];
    return { values, headers };
  }
  if (interestName === 'vehicle') {
    const interest: VehicleIdentified[] = dataValues;
    const values = interest.map((vehicle) => [
      { field: vehicle.name, cellWidth: 'auto' },
      { field: vehicle.description, cellWidth: 'auto' },
      { field: vehicle.plate, cellWidth: 'auto' },
      { field: vehicle.organization, cellWidth: 'auto' },
      { field: vehicle.vehicleType.name, cellWidth: 'auto' },
      { field: vehicle.color?.name, cellWidth: 'auto' },
      { field: vehicle.brand.name, cellWidth: 'auto' },
    ]);
    const headers = ['Name', 'Description', 'Plate', 'Organization', 'Vehicle Type', 'Color', 'Brand'];
    return { values, headers };
  }
  if (interestName === 'location') {
    const interest: LocationIdentifiedDataType[] = dataValues;
    const values = interest.map((location) => [
      { field: location.name, cellWidth: 'auto' },
      { field: location.description, cellWidth: 'auto' },
      { field: location.lat, cellWidth: 'auto' },
      { field: location.lng, cellWidth: 'auto' },
      { field: location?.aliases?.map((alias) => alias.value).join(', '), cellWidth: 'auto' },
      { field: location.address, cellWidth: 'auto' },
      { field: location.locationRecognized.name, cellWidth: 'auto' },
    ]);
    const headers = ['Name', 'Description', 'Lat', 'Lng', 'Aliases', 'Address', 'Country'];
    return { values, headers };
  }
  if (interestName === 'sensor') {
    const interest: SensorIdentified[] = dataValues;

    const values = interest.map((sensor) => [
      { field: sensor.name, cellWidth: 'auto' },
      { field: sensor.deviceName, cellWidth: 'auto' },
      { field: sensor.temperature, cellWidth: 'auto' },
      { field: sensor.description, cellWidth: 'auto' },
      { field: sensor.url, cellWidth: 'auto' },
      { field: sensor.ipAddress, cellWidth: 'auto' },
      { field: sensor.macAddress, cellWidth: 'auto' },
      { field: getEnumKeyByValue(SensorType, sensor?.type), cellWidth: 'auto' },
      { field: getEnumKeyByValue(SensorCategoryType, sensor?.categoryType), cellWidth: 'auto' },
      { field: sensor?.locationIdentifyClass?.name, cellWidth: 'auto' },
    ]);
    const headers = [
      'Name',
      'Device Name',
      'Temperature',
      'Description',
      'Url',
      'IP Address',
      'MAC Address',
      'Type',
      'Category',
      'Location',
    ];
    return { values, headers };
  }
  if (interestName === 'enterprise') {
    const interest: EnterpriseIdentifiedDataType[] = dataValues;

    const values = interest.map((enterprise) => [
      { field: enterprise?.company?.name, cellWidth: 'auto' },
      { field: enterprise?.legalCompanyName, cellWidth: 'auto' },
      { field: enterprise?.companyNumber, cellWidth: 'auto' },
      { field: enterprise?.website, cellWidth: 'auto' },
      { field: enterprise?.DUNS, cellWidth: 'auto' },
      { field: enterprise?.registryId, cellWidth: 'auto' },
      { field: enterprise?.isBlackListed, cellWidth: 'auto' },
      { field: enterprise?.emails?.join(', '), cellWidth: 'auto' },
      { field: enterprise?.industry.name, cellWidth: 'auto' },
      { field: enterprise?.country?.name, cellWidth: 'auto' },
    ]);
    const headers = [
      'Company Name',
      'Legal Company Name',
      'Company Number',
      'Website',
      'DUNS',
      'Registry Id',
      'Is Blacklisted',
      'Emails',
      'Is First Company',
      'Industry',
      'Country',
    ];
    return { values, headers };
  }
  if (interestName === 'topic') {
    const interest: TopicIdentifiedDataType[] = dataValues;
    const values = interest.map((topic) => [{ field: topic?.name, cellWidth: 'auto' }]);
    const headers = ['Name'];
    return { values, headers };
  }
  if (interestName === 'relation') {
    const tableDataSource = convertInteractionsToTableData(dataValues);

    const values = tableDataSource?.map((relation) => [
      { field: relation.name, cellWidth: 'auto' },
      { field: relation.fromInterestType, cellWidth: 'auto' },
      { field: relation.fromInterest, cellWidth: 'auto' },
      { field: relation.relation, cellWidth: 'auto' },
      { field: relation.toInterestType, cellWidth: 'auto' },
      { field: relation.toInterest, cellWidth: 'auto' },
    ]);
    const headers = [
      'Name',
      'Source Interest Type',
      'Source Interest',
      'Relation',
      'Target Interest Type',
      'Target Interest',
    ];
    return { values, headers };
  }

  if (interestName === 'nSearch') {
    const nSearch: NSearchLoggerType[] = dataValues;
    const headers = [
      'User',
      'Source Category',
      'Source Type',
      'Source',
      'Interests',
      'Media Type',
      // "Location",
      'Action Date',
    ];

    const values = nSearch.map((logData) => [
      { field: logData.username, cellWidth: 'auto' },
      { field: logData.dataSourceCategory?.join('\n'), cellWidth: 'auto' },
      { field: logData.dataSourceType?.join('\n'), cellWidth: 'auto' },
      { field: logData.dataSource?.join('\n'), cellWidth: 'auto' },
      { field: logData.interests?.join('\n'), cellWidth: 'auto' },
      { field: logData.mediaTypes?.join('\n'), cellWidth: 'auto' },
      // logData.coordinates?.map(coordinate => [coordinate.lat.toFixed(6), coordinate.lng.toFixed(6)].join(", ")).join("\n"),
      { field: convertDateToReadableText(logData.createdAt), cellWidth: 'auto' },
    ]);

    return { values, headers };
  }
  if (interestName === 'student') {
    const students: StudentDataType[] = dataValues;
    const headers = [
      'Student Name',
      'Rank',
      'Branch',
      'University Name',
      'Year Of Program',
      'Start Year',
      'Expected Graduation Year',
      'Graduation Year',
    ];

    const values = students.map((student) => [
      student?.studentName,
      student?.rank,
      student?.branch,
      student?.universityName,
      student?.yearOfProgram,
      student?.startYear,
      student?.expectedGraduationYear,
      student?.graduationYear,
    ]);

    return { values, headers };
  }
  if (interestName === 'transaction') {
    const transactions: TransactionIdentifiedDataType[] = dataValues;
    const headers = ['Name', 'Source Interest', 'Target Interest', 'Amount'];
    const values = transactions.map((transaction) => [
      transaction.name,
      getInterestNameFromLookup(transaction?.fromInterest),
      getInterestNameFromLookup(transaction?.toInterest),
      // `${transaction.amount} ${transaction.currency.name}`,
    ]);

    return { values, headers };
  }
};

export const ExportInterestForm: React.FC<IFormProps & InterestExportFormType> = ({
  form,
  filteredInterestIds,
  interestName,
}) => {
  const [usersToMail, setUsersToMail] = useState<{ label: string; value: string; key: number }[]>([]);
  const mailList = useWatch('mails', form);
  const sendAttachment = useWatch('sendAttachment', form);
  const studentType = useWatch('studentType', form);
  const [query, setQuery] = useState<any>();
  const { customerId }: userReduxType.StateType = useAppSelector((state) => state.user);
  const { theme } = useAppSelector((state) => state.configContent);

  const [getPersons] = useLazyGetPersonsIdentifiedAllQuery();
  const [getVehicles] = useLazyGetVehiclesIdentifiedAllQuery();
  const [getLocations] = useLazyGetLocationsIdentifiedAllQuery();
  const [getEnterprises] = useLazyGetEnterprisesAllQuery();
  const [getSensors] = useLazyGetSensorsIdentifiedQuery();
  const [getTopics] = useLazyGetTopicsIdentifiedQuery();
  const [getRelations] = useLazyGetInteractionsQuery();
  const [getStudents] = useLazyGetStudentsQuery();
  const [getTransactions] = useLazyGetTransactionsIdentifiedQuery();

  const { data: userList } = useGetUsersQuery({
    customerIds: customerId ?? undefined,
  });

  const getNSearches = async () => {
    try {
      const { data } = await getNSearchLogs();
      return data;
    } catch (error) {
      console.log('error', error);
    }
  };
  useEffect(() => {
    if (isDefined(userList)) {
      const mailsAndNames = userList.rows
        .filter((user) => user?.email)
        .map((user, _) => ({
          label: user.fullname,
          value: user.email,
          key: _,
        }));
      setUsersToMail(mailsAndNames);
    }
  }, [userList]);

  const mutations = {
    person: useExportPersonIdentifiedExcellMutation(),
    vehicle: useExportVehicleIdentifiedExcellMutation(),
    location: useExportLocationIdentifiedExcellMutation(),
    sensor: useExportSensorIdentifiedExcellMutation(),
    enterprise: useExportEnterpriseIdentifiedExcellMutation(),
    topic: useExportTopicIdentifiedExcellMutation(),
    nSearch: useExportNSearchExcellMutation(),
    relation: useExportRelationExcellMutation(),
    student: useExportStudentExcellMutation(),
    transaction: useExportTransactionIdentifiedExcellMutation(),
  };

  const queries = {
    person: getPersons,
    vehicle: getVehicles,
    location: getLocations,
    sensor: getSensors,
    enterprise: getEnterprises,
    topic: getTopics,
    relation: getRelations,
    student: getStudents,
    transaction: getTransactions,
  };

  useEffect(() => {
    const getQuery = async () => {
      if (interestName !== 'nSearch') {
        if (interestName === 'student') {
          setQuery(
            (
              await queries[interestName]({
                graduationApproved: studentType === 'archived',
              })
            ).data,
          );
        } else if (interestName === 'location') {
          setQuery((await queries[interestName]({ isInterested: true })).data);
        } else if (interestName === 'enterprise') {
          setQuery((await queries[interestName]({ customerId })).data);
        } else {
          setQuery((await queries[interestName]()).data);
        }
      } else {
        setQuery(await getNSearches());
      }
    };

    getQuery();
  }, [interestName, studentType]);

  const [exportExcell] = mutations[interestName];

  const onFinish = async (values: any) => {
    try {
      if (values.format === 'pdf' && query) {
        const filtered =
          values.filters === 'yes' && filteredInterestIds.length > 0
            ? query.rows.filter((interest: any) => filteredInterestIds.includes(interest.id))
            : query.rows;

        const tableDataValues = allInterestTableValues(filtered, interestName);
        if (tableDataValues) {
          const pdf = pdfExport({
            headers: tableDataValues.headers,
            tableBodyValues: tableDataValues.values,
            // interestName,
            options: {
              orientation: 'landscape',
              format: 'a4',
            },
            // isReturnFunc: true,
            headStyles: {
              fillColor: theme.colors.quoteBgc,
              textColor: tinycolor(theme.colors.quoteBgc).isDark() ? '#fff' : '#000',
            },
          });
          fileDownload(pdf, `${interestName}.pdf`);
          if (mailList?.length > 0) {
            exportFileSendMail({
              emails: mailList,
              type: 'pdf',
              ...(sendAttachment && { pdf, pdfName: `${interestName}.pdf` }),
            });
          }
        }
      }
      if (values.format === 'xlsx' && query) {
        const data = await exportExcell({
          ...(filteredInterestIds.length > 0 && values.filters === 'yes' && { ids: filteredInterestIds }),
          ...(studentType && { graduatedOrNot: studentType === 'archived' }),
        }).unwrap();
        // .catch((err) => message.error(err));
        if (data) {
          fileDownload(data, `${interestName}.${values.format}`);
        }
        if (mailList?.length > 0) {
          exportFileSendMail({
            emails: mailList,
            type: 'xlsx',
            ...(sendAttachment && { excel: data, excelName: `${interestName}.xlsx` }),
          });
        }
      }
    } catch (error) {
      console.log('err: ', error);
    }
  };
  return (
    <FormComponent options={{ form, onFinish }}>
      <Form.Item label="Export Type" name="format" rules={[{ required: true }]}>
        <Radio.Group rootClassName={styles.radioGroup}>
          <Radio rootClassName={styles.radio} value="xlsx">
            .xlsx
          </Radio>
          <Radio value="pdf" rootClassName={styles.radio}>
            .pdf
          </Radio>
        </Radio.Group>
      </Form.Item>
      {interestName === 'student' && (
        <Form.Item label="Student Type" name="studentType" rules={[{ required: true }]}>
          <Radio.Group rootClassName={styles.radioGroup}>
            <Radio rootClassName={styles.radio} value="archived">
              Archived
            </Radio>
            <Radio value="student" rootClassName={styles.radio} style={{ marginLeft: '1em' }}>
              Students
            </Radio>
          </Radio.Group>
        </Form.Item>
      )}
      {filteredInterestIds?.length > 0 && (
        <Form.Item label="Use Current Filters" name="filters" rules={[{ required: true }]}>
          <Radio.Group rootClassName={styles.radioGroup}>
            <Radio rootClassName={styles.radio} value="yes">
              Yes
            </Radio>
            <Radio value="no" rootClassName={styles.radio} style={{ marginLeft: '1em' }}>
              No
            </Radio>
          </Radio.Group>
        </Form.Item>
      )}
      <Form.Item label="Email Users" name="mails">
        <SearchableSelect
          style={{ background: 'transparent !important' }}
          mode="multiple"
          variant="borderless"
          options={usersToMail}
          placeholder="Select Users to Mail"
        />
      </Form.Item>
      {mailList?.length > 0 && (
        <Form.Item label="Send Attachment" name="sendAttachment" initialValue>
          <Radio.Group rootClassName={styles.radioGroup}>
            <Radio rootClassName={styles.radio} value>
              Yes
            </Radio>
            <Radio value={false} rootClassName={styles.radio}>
              No
            </Radio>
          </Radio.Group>
        </Form.Item>
      )}
    </FormComponent>
  );
};
